/**
 * Component responsible for store finder list behaviour
 *
 * @author Tamara Bazko
 */
import $ from 'jquery';
import Mustache from 'mustache';
import Singleton from 'components/utils/Singleton';
import EventTypes from 'components/EventTypes';
import createLogger from 'components/logger/Logger';
import StoreFinderComponent from './StoreFinderComponent';
import matchBreakpoint from 'components/utils/matchBreakpoint';
import device from 'components/utils/device';
import listItemTemplate from './templates/storeListItem.html';
import { gsap, ScrollToPlugin } from 'gsap/all';
import 'jquery.extendPrototype';

const Logger = createLogger('List');
gsap.registerPlugin(ScrollToPlugin);

function StoreFinderList(settings) {
  Logger.info('Created');

  StoreFinderComponent.call(this, settings, module);

  this.$storeDetails = this.findStoreFinderElement(this.settings.storeDetailsSelector);
  this.$scrollContainer = this.findStoreFinderElement(this.settings.scrollContainerSelector);
  this.$storeFinderBar = this.findStoreFinderElement(this.settings.storeFinderBarSelector);
  this.$scrollContainerTitle = this.findStoreFinderElement(
    this.settings.scrollContainerTitleSelector
  );
  this.offsetTop = this.$element.position().top;
  this.bindEvents();
}
$.extendPrototype(StoreFinderList, StoreFinderComponent.prototype, {
  // Item that's currently active in list
  $activeItem: undefined,

  // The scrollable container for this list
  $scrollContainer: undefined,
  $storeFinderBar: undefined,

  // Container
  settings: {
    selector: '.js-storeFinder-bar-list',
    scrollContainerSelector: '.js-storeFinder-bar-scrollArea',
    scrollContainerTitleSelector: '.js-storeFinder-bar-scrollArea-title',
    storeDetailsSelector: '.js-storeFinder-details',
    storeFinderBarSelector: '.js-storeFinder-bar',
  },

  loadingTemplate: '<div class="loading" style="height: 24px;"></div>',

  offsetTop: 0,

  // Array with store data objects
  stores: [],

  /**
   * Bind events
   */
  bindEvents() {
    const onStoreSelected = this.onStoreSelected.bind(this);

    document.jq
      .on(EventTypes.STOREFINDER_DISTANCE_UPDATE, this.onGetStores.bind(this))
      .on(EventTypes.STOREFINDER_GET_STORES, this.onGetStores.bind(this))
      .on(EventTypes.STOREFINDER_STORE_DESELECTED, onStoreSelected)
      .on(EventTypes.STOREFINDER_STORE_SELECTED, onStoreSelected)
      .on(EventTypes.STOREFINDER_STORES_FILTERED, this.onGetStores.bind(this))
      .on(EventTypes.STOREFINDER_SHOW_LIST_REQUEST, this.onStoreDetailsClose.bind(this))
      .on(EventTypes.STOREFINDER_STORE_DETAILS_CLOSE, this.onStoreDetailsClose.bind(this));
  },

  /**
   * Add store to the list in the store finder bar
   * @param  {object}    store object
   */
  addStores(eventData) {
    const { stores, numResults, numAllResults } = eventData;

    Logger.debug('addStores', stores);

    const data = {
      labels: window.labels ? window.labels.storeFinder : {},
      distanceUnit: window.labels.storeFinder.distanceUnit,
      stores,
    };

    const listItemContent = Mustache.render(listItemTemplate, data);
    const $listItems = $(listItemContent);
    const title = this.$scrollContainerTitle.get(0);

    if (title) {
      const { available, availableOutsideRadius, notAvailable } = title.dataset;

      if (numResults) {
        title.textContent = available;
      } else if (!numResults && numAllResults) {
        title.textContent = availableOutsideRadius;
      } else if (!numResults && !numAllResults) {
        title.textContent = notAvailable;
      }

      this.checkTitlesAndBtn();
      this.$scrollContainerTitle.toggleClass('is-active', stores.length > 0);
    }

    if ($listItems) {
      try {
        this.$element.html($listItems);
        this.$listItem = this.$element.find('.js-storeFinder-bar-list-item');
        this.$listItem.on('vclick', this.onItemClick.bind(this));
        this.$listItem.on('keypress', ev => {
          if (ev.which === 13) {
            this.$listItem.click();
          }
        });

        if (!device.hasTouch && matchBreakpoint(this.settings.breakpointOverlay)) {
          this.$listItem
            .on('mouseenter', this.onItemHover.bind(this))
            .on('mouseleave', this.onItemHover.bind(this));
        }

        this.$element
          .find('.js-storeFinder-bar-button')
          .on('vclick', this.onItemButtonClick.bind(this));
      } catch (error) {
        Logger.error(module, error.message, $listItems);
      }
    } else {
      Logger.error(module, 'Cannot create list item from HTML:', $listItems);
    }

    this.stores = stores;
  },

  /**
   * Remove all children from list
   */
  clearList() {
    if (this.stores.length) {
      this.$element.html(this.loadingTemplate);
      this.$activeItem = undefined;
    }
  },

  /**
   * Handle event.
   * @param  {object} event     jQuery event object
   * @param  {object} eventData Data belonging to event
   */
  onGetStores(event, eventData) {
    const storesTimeout = 500;
    this.$scrollContainer.scrollTop(0);
    this.clearList();
    window.setTimeout(this.addStores.bind(this, eventData), storesTimeout);
  },

  /**
   * Event listener for when a list item is clicked.
   * @param  {object} eventData   Data belonging to event
   * @param  {object} event       jQuery event object
   */
  onItemClick(event) {
    event.preventDefault();
    if (!matchBreakpoint(this.settings.breakpointOverlay)) {
      this.$listItem.off('vclick');
    }

    const $targetItem = $(event.target).closest('.js-storeFinder-bar-list-item');
    const listItemIndex = $targetItem.index();
    const eventData = {
      store: this.stores[listItemIndex],
      $item: $targetItem,
      fromList: true,
    };

    if (!matchBreakpoint(this.settings.breakpointOverlay)) {
      this.$element.trigger(EventTypes.STOREFINDER_SHOW_MAP_REQUEST);
    }

    if (device.osName === device.ANDROID) {
      // On Android next view opens faster then click event is finished,
      // so if it lands on buttons unexpected situations occur
      setTimeout(
        function () {
          this.$element.trigger(EventTypes.STOREFINDER_STORE_DETAILS_OPEN_REQUEST, eventData);
        }.bind(this),
        250
      );
    } else {
      this.$element.trigger(EventTypes.STOREFINDER_STORE_DETAILS_OPEN_REQUEST, eventData);
    }
  },

  onItemButtonClick(event) {
    event.stopPropagation();

    const $targetItem = $(event.target).closest('.js-storeFinder-bar-list-item');
    const listItemIndex = $targetItem.index();
    const eventData = {
      store: this.stores[listItemIndex],
    };

    document.jq.trigger(EventTypes.STOREFINDER_STORE_BUTTON_CLICKED, eventData);
  },

  /**
   * Handle event.
   * @param  {object} event     jQuery event object
   * @param  {object} eventData Data belonging to event
   */
  onItemHover(event) {
    if (!device.hasTouch) {
      const $targetItem = $(event.target).closest('.js-storeFinder-bar-list-item');
      const listItemIndex = $targetItem.index();
      const isSelected = event.type === 'mouseover';
      const eventType = isSelected
        ? EventTypes.STOREFINDER_STORE_SELECTED
        : EventTypes.STOREFINDER_STORE_DESELECTED;

      this.$element.trigger(eventType, {
        store: this.stores[listItemIndex],
        isSelected,
        fromMap: false,
      });
    }
  },

  /**
   * Add vclick event listener back to list items.
   * Listeners are removed after first click, while store details overlay is
   * rendering, to prevent double click, which will break some functionality
   * (often happeded on Android)
   */
  onStoreDetailsClose() {
    if (this.$listItem) {
      this.$listItem.off('vclick').on('vclick', this.onItemClick.bind(this));
    }
  },

  /**
   * Event handler for when a store is (de)selected
   * @param  {object} event     jQuery event
   * @param  {object} eventData Event data, contains `store` and `isSelected`
   */
  onStoreSelected(event, eventData) {
    const { store } = eventData;
    const fromMap = typeof eventData.fromMap !== 'undefined' && eventData.fromMap !== false;

    if (this.$activeItem) {
      this.$activeItem.removeClass('is-active');
      this.$activeItem = undefined;
    }

    if (eventData.isSelected) {
      this.$activeItem = $('.js-storeFinder-bar-list').find(`[data-store-id="${store.store_id}"]`);
      if (this.$activeItem) {
        this.$activeItem.addClass('is-active');

        if (matchBreakpoint(this.settings.breakpointOverlay) && fromMap) {
          gsap.to(this.$scrollContainer, {
            duration: 0.4,
            scrollTo: {
              y: this.$activeItem,
            },
          });
        }
      }
    }
  },
  /**
   * Reveal the stores messages functions
   * */
  checkTitlesAndBtn() {
    const isTitleConditionsValid = this.isTitleConditionsValid();
    if (isTitleConditionsValid) {
      this.$scrollContainerTitle.toggleClass('hidden', !isTitleConditionsValid);
    }
  },
  getOptionsBarC() {
    return this.findStoreFinderElement('.js-storeFinder-bar-optionsBar');
  },
  getSizeTitles() {
    const optionsBarc = this.getOptionsBarC();
    if (optionsBarc.length > 0) {
      return Array.from(optionsBarc[0]?.querySelectorAll('.js-sizeTitle'));
    }
    return [];
  },
  validateTitles() {
    const sizeTitles = this.getSizeTitles();
    if (sizeTitles.length > 0) {
      return sizeTitles.every(title => this.hasClass(title, 'is-active'));
    }
    return false;
  },
  hasClass(component, className) {
    return component.classList.value.includes(className);
  },
  isTitleConditionsValid() {
    return (
      Boolean(this.getOptionsBarC().length) &&
      Boolean(this.getSizeTitles().length) &&
      this.validateTitles()
    );
  },
});

export default Singleton.create(StoreFinderList, 'StoreFinderList');
